import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Card, Button, Form, Col, Row, Badge, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import ShimmerUI from 'components/ShimmerUI/PatientShimmer';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';
import {
  getPatientDocuments,
  deleteDocument
} from 'helpers/documentManagerApi';
import moment from 'moment';

const DocumentList = ({
  patientId,
  availableTypes,
  loadingTypes,
  onView,
  onEdit,
  onCreate
}) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState(null);

  // Delete Confirmation Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const res = await getPatientDocuments(patientId);
      if (res && res.status) {
        const groupedData = res.data || [];
        const flatDocuments = [];
        groupedData.forEach(group => {
          const docs = group.documents || [];
          docs.forEach(doc => {
            flatDocuments.push({
              ...doc,
              document_type_id: group.document_type_id,
              type_name: group.document_type_name,
              created_by_name: doc.created_by_name || 'System'
            });
          });
        });
        setDocuments(flatDocuments);
      } else {
        toast.error(res?.message || 'Failed to load documents');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error fetching documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (patientId) {
      fetchDocuments();
    }
  }, [patientId]);

  const handleDeleteClick = doc => {
    setDocToDelete(doc);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!docToDelete) return;
    try {
      setDeleting(true);
      const res = await deleteDocument(docToDelete.id);
      if (res && res.status) {
        toast.success('Document deleted successfully');
        setShowDeleteModal(false);
        setDocToDelete(null);
        fetchDocuments();
      } else {
        toast.error(res?.message || 'Failed to delete document');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error deleting document');
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      accessorKey: 'title',
      header: 'Document Title',
      meta: {
        headerProps: { className: 'pe-1 text-900 text-start' },
        cellProps: { className: 'text-start' }
      },
      cell: ({ row: { original } }) => (
        <div className="d-flex align-items-center gap-2">
          <span className="fw-semi-bold">{original.title}</span>
          {original.schema_changed ? (
            <Badge bg="warning" className="text-dark fs-11">
              Schema Updated
            </Badge>
          ) : ''}
        </div>
      )
    },
    {
      accessorKey: 'type_name',
      header: 'Type',
      meta: {
        headerProps: { className: 'pe-1 text-900 text-start' },
        cellProps: { className: 'text-start' }
      }
    },
    {
      accessorKey: 'file_count',
      header: 'Files',
      meta: {
        headerProps: { className: 'pe-1 text-900 text-center' },
        cellProps: { className: 'text-center' }
      }
    },
    {
      accessorKey: 'created',
      header: 'Created On',
      meta: {
        headerProps: { className: 'pe-1 text-900 text-center' },
        cellProps: { className: 'text-center' }
      },
      cell: ({ row: { original } }) => (
        <span>
          {original.created ? moment(original.created).format('MM/DD/YY') : ''}
        </span>
      )
    },
    {
      accessorKey: 'created_by_name',
      header: 'Uploaded By',
      meta: {
        headerProps: { className: 'pe-1 text-900 text-start' },
        cellProps: { className: 'text-start' }
      }
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      meta: {
        headerProps: { className: 'text-900 text-center' },
        cellProps: { className: 'text-center' }
      },
      enableSorting: false,
      cell: ({ row: { original } }) => (
        <div className="d-flex justify-content-center gap-2">
          <Button
            variant="link"
            size="sm"
            className="p-0 text-info"
            onClick={() => onView(original.id)}
            title="View Details"
          >
            <FontAwesomeIcon icon="eye" />
          </Button>
          <Button
            variant="link"
            size="sm"
            className="p-0 text-primary"
            onClick={() => onEdit(original.id)}
            title="Edit Document"
          >
            <FontAwesomeIcon icon="edit" />
          </Button>
          <Button
            variant="link"
            size="sm"
            className="p-0 text-danger"
            onClick={() => handleDeleteClick(original)}
            title="Delete Document"
          >
            <FontAwesomeIcon icon="trash" />
          </Button>
        </div>
      )
    }
  ];

  const typeOptions = [
    { value: '', label: 'All Types' },
    ...availableTypes.map(t => ({ value: t.id, label: t.name }))
  ];

  const filteredDocuments = React.useMemo(() => {
    if (!selectedTypeFilter || selectedTypeFilter.value === '') {
      return documents;
    }
    return documents.filter(
      doc => String(doc.document_type_id) === String(selectedTypeFilter.value)
    );
  }, [documents, selectedTypeFilter]);

  const table = useAdvanceTable({
    data: filteredDocuments,
    columns,
    selection: false,
    sortable: true,
    pagination: true
  });

  const hasNoEnabledTypes = !loadingTypes && availableTypes.length === 0;

  return (
    <div className="border-0">
      <Card.Body className="p-0">
        <Row className="mb-3 align-items-end g-2">
          <Col md={4}>
            <Form.Group controlId="filterType">
              <Form.Label className="fs-11 fw-semi-bold">
                Filter by Type
              </Form.Label>
              <ThemedSelect
                options={typeOptions}
                value={selectedTypeFilter}
                onChange={setSelectedTypeFilter}
                placeholder="All Types"
                isClearable
                classNamePrefix="react-select"
              />
            </Form.Group>
          </Col>
          <Col
            md={3}
            className="ms-auto d-flex justify-content-end align-items-center gap-2"
          >
            <AdvanceTableProvider {...table}>
              <AdvanceTableSearchBox placeholder="Search documents..." />
            </AdvanceTableProvider>
            {!hasNoEnabledTypes && (
              <Button
                variant="primary"
                size="sm"
                onClick={onCreate}
                className="text-nowrap py-2"
              >
                <FontAwesomeIcon icon="plus" className="me-1" /> Add Document
              </Button>
            )}
          </Col>
        </Row>

        {hasNoEnabledTypes && (
          <div className="alert alert-info py-3 text-center my-3 fs-10">
            <FontAwesomeIcon icon="info-circle" className="me-2" />
            No document types are currently enabled for this facility group.
            Admin configuration required.
          </div>
        )}

        {loading ? (
          <ShimmerUI />
        ) : documents.length === 0 ? (
          <div className="text-center py-5 border border-dashed rounded bg-light my-3">
            <FontAwesomeIcon icon="file-alt" className="fs-4 text-300 mb-2" />
            <p className="text-500 mb-0 fs-10">
              No documents found for this patient.
            </p>
          </div>
        ) : (
          <AdvanceTableProvider {...table}>
            <AdvanceTable
              headerClassName="bg-200 text-900 align-middle"
              rowClassName="align-middle"
              tableProps={{
                striped: true,
                className: 'fs-10 mb-0 overflow-hidden'
              }}
            />
            <div className="mt-3">
              <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
            </div>
          </AdvanceTableProvider>
        )}
      </Card.Body>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-9 text-danger">
            Delete Document
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="fs-10">
          <p className="mb-0">
            Are you sure you want to delete the document{' '}
            <strong>&quot;{docToDelete?.title}&quot;</strong>? This action
            cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={handleConfirmDelete}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

DocumentList.propTypes = {
  patientId: PropTypes.number.isRequired,
  availableTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  loadingTypes: PropTypes.bool.isRequired,
  onView: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired
};

export default DocumentList;
